import React from "react";
import { Link } from "react-router-dom";
import { PageHero, Section, Eyebrow, SectionTitle } from "../components/Primitives";
import { HandHeart, Utensils, HomeIcon as Bed, Sparkles, Landmark, HeartHandshake } from "lucide-react";

const tiers = [
  { amount: "$15", t: "Feeds a family", d: "Provides a warm vegetarian meal for a family of four at our Sunday community lunch." },
  { amount: "$45", t: "A night of shelter", d: "Covers one night of safe, dignified accommodation at Rahula Community Lodge." },
  { amount: "$120", t: "A month of language class", d: "Sponsors a child’s Saturday Vietnamese language and culture class for a full month." },
  { amount: "$500", t: "An emergency relief grant", d: "Funds a small, no-questions-asked grant to a family in crisis — rent, groceries or medicine." },
];

const Support = () => (
  <>
    <PageHero
      eyebrow="Ways to Give"
      title="Every gift, no matter its size, is held with care."
      subtitle="We are a registered Australian charity. Donations are used simply, transparently and gratefully — to feed, shelter and support the people who need us."
    />

    <Section>
      <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
        {tiers.map((t) => (
          <div key={t.amount} className="p-8 border border-[#e9dcc0] bg-[#fdfaf3] hover:border-[#7a2e1f] transition-colors duration-300">
            <div className="font-serif text-4xl text-[#7a2e1f]">{t.amount}</div>
            <h3 className="font-serif text-xl text-[#3d2817] mt-3">{t.t}</h3>
            <p className="mt-3 text-[14px] text-[#5a4a3a] leading-relaxed">{t.d}</p>
          </div>
        ))}
      </div>

      <div className="mt-16 grid lg:grid-cols-2 gap-10">
        <div className="bg-[#f6efe0] border border-[#e9dcc0] p-10">
          <Landmark size={26} className="text-[#7a2e1f]" strokeWidth={1.5} />
          <h3 className="font-serif text-3xl text-[#3d2817] mt-4">Direct deposit</h3>
          <p className="mt-3 text-[#5a4a3a] leading-relaxed">The simplest way to give. Please email us for our current banking details or to arrange a regular monthly gift.</p>
          <a href="mailto:info@vanhanhcanberra.com?subject=Donation%20enquiry" className="btn-primary mt-6">Email for details</a>
        </div>
        <div className="bg-[#f6efe0] border border-[#e9dcc0] p-10">
          <HeartHandshake size={26} className="text-[#7a2e1f]" strokeWidth={1.5} />
          <h3 className="font-serif text-3xl text-[#3d2817] mt-4">Give in person</h3>
          <p className="mt-3 text-[#5a4a3a] leading-relaxed">Donation bowls sit at the entrance to the meditation hall. Every coin, every note, every kind word matters.</p>
          <Link to="/visit" className="btn-outline mt-6">Plan a visit</Link>
        </div>
      </div>
    </Section>

    <section className="bg-[#fdfaf3] border-t border-[#e9dcc0]">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24">
        <div className="text-center mb-14">
          <Eyebrow>Other ways</Eyebrow>
          <SectionTitle className="mt-6">Gifts of time, skills and things.</SectionTitle>
        </div>
        <div className="grid md:grid-cols-3 gap-8">
          {[
            { Icon: HandHeart, t: "Volunteer with us", d: "Cook a Sunday lunch, drive an elder to appointments, help in the library or the garden." },
            { Icon: Utensils, t: "Donate food & goods", d: "We accept non-perishable food, warm blankets, unopened toiletries and children’s books." },
            { Icon: Bed, t: "Sponsor a bed", d: "Underwrite a bed at Rahula Lodge for a month or a year — keeping shelter open to all." },
            { Icon: Sparkles, t: "Leave a gift in your will", d: "A quiet, generous way to keep the monastery serving Canberra for another forty years." },
            { Icon: HeartHandshake, t: "Corporate partnership", d: "Workplace matching, in-kind support and community volunteering days — we’d love to talk." },
            { Icon: Landmark, t: "Grants & foundations", d: "Please contact us for our latest annual report, ACNC statement and program outcomes." },
          ].map(({ Icon, t, d }) => (
            <div key={t} className="p-8 border border-[#e9dcc0]">
              <Icon size={24} className="text-[#7a2e1f]" strokeWidth={1.5} />
              <h3 className="font-serif text-xl text-[#3d2817] mt-4">{t}</h3>
              <p className="mt-3 text-[15px] text-[#5a4a3a] leading-relaxed">{d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>

    <section className="bg-[#7a2e1f] text-[#f6efe0]">
      <div className="max-w-4xl mx-auto px-6 lg:px-10 py-20 text-center">
        <p className="font-serif text-3xl md:text-4xl leading-tight">Thank you. Truly. Every gift becomes a meal, a bed, a quiet hour of study, a hand held.</p>
        <p className="mt-6 text-sm uppercase tracking-[0.3em] text-[#c9a15b]">ABN 20 648 948 015 · Registered with the ACNC</p>
      </div>
    </section>
  </>
);

export default Support;
